import { forwardRef, memo, Ref } from 'react';

import clsx from 'clsx';
import { Editor as SlateEditor } from 'slate';
import { Slate } from 'slate-react';

import ElementNode from '../ElementNode';
import LeafNode from '../LeafNode';
import Toolbar from '../Toolbar';
import { Body, Error, richTextEditorClasses, Root, StyledEditable } from './styles';
import { EditorProps } from './types';
import useEditor from './vm';

export type { EditorProps };

const Editor = (
  {
    className,
    classes,
    children,
    spellCheck,
    placeholder,
    i18n,
    renderCreateFormulaOverlay,
    toolbar = {
      canFormatText: true,
      canInsertFormula: true,
      canUploadImage: true,
    },
    showErrors = true,
    errorMessage,
    readOnly = false,
    autoFocus = false,
    ...vmProps
  }: EditorProps,
  ref: Ref<SlateEditor>
) => {
  const {
    editor,
    onBlur: handleBlur,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    value,
  } = useEditor(vmProps, ref);

  return (
    <Root className={clsx(classes?.root, className)}>
      <Slate editor={editor} value={value} onChange={handleChange}>
        <Body
          className={clsx(richTextEditorClasses.body, classes?.body, {
            [richTextEditorClasses.error]: errorMessage,
          })}
        >
          <StyledEditable
            spellCheck={spellCheck}
            renderElement={ElementNode}
            renderLeaf={LeafNode}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className={clsx(richTextEditorClasses.editable, classes?.editable)}
            readOnly={readOnly}
            autoFocus={autoFocus}
          />
          {children}
        </Body>
        {showErrors && errorMessage && <Error className={classes?.errorMessage}>{errorMessage}</Error>}
        {toolbar !== false && (
          <Toolbar
            {...toolbar}
            i18n={i18n}
            className={classes?.toolbar}
            classes={{
              ...classes,
              root: classes?.toolbar,
            }}
            renderCreateFormulaOverlay={renderCreateFormulaOverlay}
          />
        )}
      </Slate>
    </Root>
  );
};

export default memo(forwardRef(Editor));
